import config from '@config';
import apiConfig from './apiConfig';
import invokeApi from './network';

/**
 *
 * @param {object} payload contains otpType and mobileNumber
 * @returns api promise to be resolved by react query
 */
export const apiLogin = payload => {
  const { otpType, mobileNumber } = payload;
  return invokeApi(
    apiConfig.login,
    config.API_URL,
    {
      queryProps: {
        otpType,
        mobileNumber,
      },
    },
    null,
    null,
    null,
  );
};

/**
 *
 * @param {object} payload
 * @param {string} token
 * @returns api promise to be resolved by react query
 */
export const apiOTP = (payload, token) =>
  invokeApi(apiConfig.otp, config.API_URL, null, { 'x-otp-token': token }, payload, null);

/**
 *
 * @returns api promise to be resolved by react query
 */
export const apiLogout = () => invokeApi(apiConfig.logout, config.API_URL, null, null, null, null);

export const apiMe = () => invokeApi(apiConfig.me, config.API_URL, null, null, null, null);

export const apiReasons = payload => {
  const {
    // limit, skip,
    accountNumber,
    reasonType,
  } = payload;
  return invokeApi(
    apiConfig.reasons,
    config.API_URL,
    {
      queryProps: {
        // limit,
        // skip,
        reasonType,
      },
      mixedProps: {
        accountNumber,
      },
    },
    null,
    null,
    null,
  );
};

export const apiPaymentCycles = payload => {
  const { limit, skip } = payload;
  return invokeApi(
    apiConfig.paymentCycles,
    config.API_URL,
    {
      queryProps: {
        limit,
        skip,
      },
    },
    null,
    null,
    null,
  );
};

export const apiTransactionSummary = payload => {
  const { accountNumber, paymentCycleId } = payload;

  return invokeApi(
    apiConfig.transactionSummary,
    config.API_URL,
    {
      queryProps: {
        paymentCycleId,
      },
      mixedProps: {
        accountNumber,
      },
    },
    null,
    null,
    null,
  );
};

export const apiTransactionHistory = payload => {
  const { accountNumber, limit, skip, paymentCycleId } = payload;

  return invokeApi(
    apiConfig.transactionsHistory,
    config.API_URL,
    {
      mixedProps: {
        accountNumber,
      },
    },
    null,
    {
      limit,
      skip,
      paymentCycleId,
    },
    null,
  );
};

export const apiMarketingBanners = () => invokeApi(apiConfig.marketingBanner, config.API_URL, null, null, null, null);

export const apiBillPayment = payload => {
  const { accountTypeId } = payload;
  return invokeApi(apiConfig.billPayment, config.API_URL, { queryProps: { accountTypeId } }, null, null, null);
};

export const apiTopProducts = () => invokeApi(apiConfig.topProducts, config.API_URL, null, null, null, null);

export const apiFavourites = () => invokeApi(apiConfig.favourites, config.API_URL, null, null, null, null);

const getNotificationCategoryParam = (category?: string): string | undefined => {
  if (!category) return category;

  return category.toUpperCase();
};

export const apiReferralValidation = code =>
  invokeApi(apiConfig.referralValidation, config.API_URL, { queryProps: { code } }, null, null, null);

export const apiSubProductsOrNominals = payload => {
  const { billAccountNumber, accountTypeId, productId } = payload;
  return invokeApi(
    apiConfig.subProductsOrNominals,
    config.API_URL,
    { queryProps: { billAccountNumber, accountTypeId }, mixedProps: { productId } },
    null,
    payload,
    null,
  );
};

export const apiNumberHistory = payload => {
  const { productPrefix } = payload;
  return invokeApi(apiConfig.numberHistory, config.API_URL, { queryProps: { productPrefix } }, null, null, null);
};

export const apiBPRequest = payload => invokeApi(apiConfig.bpRequest, config.API_URL, null, null, payload, null);

export const apiBPConfirm = payload => invokeApi(apiConfig.bpConfirm, config.API_URL, null, null, payload, null);

export const apiBanksEnrolled = () => invokeApi(apiConfig.banksEnrolled, config.API_URL, null, null, null, null);

export const apiWithdrawalRequest = payload =>
  invokeApi(apiConfig.withdrawRequest, config.API_URL, null, null, payload, null);

export const apiWithdrawalConfirm = payload =>
  invokeApi(apiConfig.withdrawConfirm, config.API_URL, null, null, payload, null);

export const apiToUploadImage = payload =>
  invokeApi(apiConfig.imageUpload, config.API_URL, null, { 'Content-Type': 'multipart/form-data' }, payload, null);

export const apiToDeleteImage = payload =>
  invokeApi(
    apiConfig.deletImage,
    config.API_URL,
    {
      mixedProps: { documentId: payload.documentId },
    },
    null,
    null,
    null,
  );

export const apiToEditTransactions = payload => {
  const { description, transactionReasonId, transactionHistoryId, accountNumber } = payload;

  return invokeApi(
    apiConfig.updateTransaction,
    config.API_URL,
    {
      mixedProps: { transactionHistoryId },
    },
    null,
    { description, transactionReasonId, accountNumber },
    null,
  );
};

export const apiForAutoDisburse = payload => {
  const { employeeId } = payload;
  return invokeApi(
    apiConfig.autoDisburseDetails,
    config.API_URL,
    {
      mixedProps: {
        employeeId,
      },
    },
    null,
    null,
    null,
  );
};

export const apiSecurityPinSimpleRegistration = payload =>
  invokeApi(apiConfig.securityPinSimpleRegistration, config.API_URL, null, null, { securityPin: payload }, null);

export const apiSecurityPinVerify = payload =>
  invokeApi(apiConfig.securityPinVerify, config.API_URL, null, null, { securityPin: payload }, null);

export const apiSecurityPinOpenVerify = payload =>
  invokeApi(
    apiConfig.securityPinOpenVerify,
    config.API_URL,
    null,
    null,
    { token: payload.token, securityPin: payload.securityPin },
    null,
  );

export const registerBiometric = (bioToken: string) =>
  invokeApi(apiConfig.securityPinBiometric, config.API_URL, null, null, { bioToken }, null);

export const apiSecurityPinBiometricV2 = (payload: { token: string; bioToken: string }) =>
  invokeApi(apiConfig.securityPinBiometricV2, config.API_URL, null, null, payload, null);

export const apiPhoneNumberChange = payload =>
  invokeApi(
    apiConfig.phoneNumberChange,
    config.API_URL,
    null,
    null,
    {
      token: payload.token,
      mobileNumber: payload.mobileNumber,
      otpType: payload.otpType,
    },
    null,
  );

export const apiPhoneNumberChangeConfirm = payload =>
  invokeApi(
    apiConfig.phoneNumberChangeConfirm,
    config.API_URL,
    null,
    null,
    {
      token: payload.token,
      otpPin: payload.otpPin,
    },
    null,
  );

export const apiForPicEmployeeReferralList = payload => {
  const { employeeId } = payload;
  return invokeApi(
    apiConfig.picEmployeeReferralList,
    config.API_URL,
    {
      mixedProps: {
        employeeId,
      },
    },
    null,
    null,
    null,
  );
};

export const apiForShareEmployeeReferral = payload =>
  invokeApi(apiConfig.employerShareReferral, config.API_URL, null, null, payload, null);

export const apiProfile = () => invokeApi(apiConfig.profile, config.API_URL, null, null, null, null);

export const apiBankList = payload => {
  const { limit, skip } = payload;

  return invokeApi(apiConfig.allBanks, config.API_URL, { queryProps: { limit, skip } }, null, null, null);
};

export const addEmployeeBeneficiary = payload =>
  invokeApi(apiConfig.addBank, config.API_URL, null, null, payload, null);

export const addEmployeeBeneficiaryV2 = payload =>
  invokeApi(apiConfig.addBankV2, config.API_URL, null, null, payload, null);

export const deleteEmployeeBeneficiary = payload =>
  invokeApi(
    apiConfig.deleteBank,
    config.API_URL,
    {
      mixedProps: { beneficiaryId: payload },
    },
    null,
    payload,
    null,
  );

export const apiGetPhoneList = () => invokeApi(apiConfig.phoneNumberList, config.API_URL, null, null, null, null);

export const addPhoneForUser = payload => invokeApi(apiConfig.addPhone, config.API_URL, null, null, payload, null);

export const addPhoneForUserV2 = payload => invokeApi(apiConfig.addPhoneV2, config.API_URL, null, null, payload, null);

export const deletePhoneNumber = payload =>
  invokeApi(apiConfig.deletePhone, config.API_URL, { mixedProps: { phoneNumberId: payload } }, null, payload, null);

export const getSalaryInfo = () => invokeApi(apiConfig.salaryInfo, config.API_URL, null, null, null, null);

export const getLogs = () => invokeApi(apiConfig.logInfo, config.API_URL, null, null, null, null);

export const checkTimeout = () => invokeApi(apiConfig.testTimeOut, config.API_URL, null, null, null, null);

export const getAppUpdates = () => invokeApi(apiConfig.appUpdate, config.API_URL, null, null, null, null);
export const getRoles = () => invokeApi(apiConfig.fetchRoles, config.API_URL, null, null, null, null);

export const getDepartmentNames = () => invokeApi(apiConfig.fetchDepartments, config.API_URL, null, null, null, null);

export const getSecurityPinInvalidAttempts = () =>
  invokeApi(apiConfig.pinInvalidAttempts, config.API_URL, null, null, null, null);

export const getSecurityPinOpen = () => invokeApi(apiConfig.securityPinOpenV2, config.API_URL, null, null, null, null);

export const getSecurityPinOpenV2 = () =>
  invokeApi(apiConfig.securityPinOpenV2, config.API_URL, null, null, null, null);

export const getFeatureFlags = () => invokeApi(apiConfig.featureFlag, config.API_URL, null, null, null, null);
export const getEarnedReferrals = () =>
  invokeApi(apiConfig.fetchEarnedReferrals, config.API_URL, null, null, null, null);

export const getReferralTNC = () => invokeApi(apiConfig.fetchReferralTNC, config.API_URL, null, null, null, null);

export const setOptInForAutoDisburse = payload =>
  invokeApi(apiConfig.setOptIn, config.API_URL, null, null, payload, null);

export const getUserProperties = () => invokeApi(apiConfig.userProperties, config.API_URL, null, null, null, null);

export const updateDeparmentsDesignationApi = payload =>
  invokeApi(apiConfig.updateDepartments, config.API_URL, null, null, payload, null);

export const addRFavourites = payload =>
  invokeApi(apiConfig.addRemoveFavourites, config.API_URL, null, null, payload, null);

export const apiGetNotificationPreferences = () =>
  invokeApi(apiConfig.notificationPreferences, config.API_URL, null, null, null, null);

export const apiUpdateNotificationPreferences = payload =>
  invokeApi(apiConfig.updateNotificationPreferences, config.API_URL, null, null, payload, null);

export const apiMarkAllNotificationsRead = () =>
  invokeApi(apiConfig.markAllNotificationsRead, config.API_URL, null, null, null, null);

export const apiMarkNotificationsRead = payload =>
  invokeApi(apiConfig.markNotificationsRead, config.API_URL, null, null, payload, null);

export const apiNotifications = payload => {
  const { category, limit, skip } = payload;

  return invokeApi(
    apiConfig.notifications,
    config.API_URL,
    {
      queryProps: {
        skip,
        limit,
        category: getNotificationCategoryParam(category),
      },
    },
    null,
    null,
    null,
  );
};

export interface ShortLinkPayload {
  originalUrl: string;
  createdBy: number;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

export const apiCreateShortLink = (payload: ShortLinkPayload) =>
  invokeApi(apiConfig.shortLink, config.API_URL, null, null, payload, null);
